import { formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { GitCommitHorizontal, MoreHorizontal } from 'lucide-react'
import { WorkspaceFileIcon, formatFileSize, workspaceFileVisual } from '@/frontend/features/Fde/WorkspaceFileVisual'
import type { FdeCopy, RepositoryFile } from '@/frontend/features/Fde/types'
import { cn } from '@/lib/utils'

export default function WorkspaceFileCard({ file, view, copy, onOpen }: {
  file: RepositoryFile
  view: 'grid' | 'list'
  copy: FdeCopy
  onOpen: () => void
}) {
  const visual = workspaceFileVisual(file)
  const author = file.created_commit?.author_name || file.latest_commit?.author_name || copy.material.unknown_author
  const when = file.latest_commit?.committed_at
    ? formatDistanceToNow(parseISO(file.latest_commit.committed_at), { addSuffix: true, locale: ptBR })
    : copy.material.no_version
  const versionLabel = copy.material.version_count.replace('%{count}', String(file.version_count))

  return <button
    type="button"
    onClick={onOpen}
    className={cn(
      'group min-w-0 text-left transition-all hover:border-primary/35 hover:bg-primary/[0.025] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
      view === 'grid' ? 'rounded-xl border border-border bg-card p-3.5 shadow-xs' : 'grid w-full grid-cols-[minmax(0,1fr)_minmax(8rem,0.45fr)_7rem_2rem] items-center gap-3 border-b border-border px-3 py-2.5',
    )}
  >
    <span className="flex min-w-0 items-center gap-3">
      <WorkspaceFileIcon file={file} />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-foreground">{file.name}</span>
        <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{view === 'grid' ? file.directory || copy.material.root_folder : `${author} · ${when}`}</span>
      </span>
    </span>
    {view === 'list' && <span className="truncate text-xs text-muted-foreground">{author}</span>}
    {view === 'list' && <span className="text-xs text-muted-foreground">{formatFileSize(file.byte_size)}</span>}
    {view === 'list' && <MoreHorizontal className="size-4 text-muted-foreground opacity-0 group-hover:opacity-100" />}
    {view === 'grid' && <span className="mt-4 flex items-center justify-between border-t border-border/70 pt-2.5 text-[10px] text-muted-foreground">
      <span className="flex min-w-0 items-center gap-1.5"><GitCommitHorizontal className="size-3" /><span className="truncate">{author} · {when}</span></span>
      <span className="ml-2 shrink-0 rounded bg-muted px-1.5 py-0.5 font-medium">{visual.label} · {versionLabel}</span>
    </span>}
  </button>
}
